import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import { api } from "@/lib/api";

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-obsidian-900">
      <SiteHeader />
      <main className="max-w-xl mx-auto px-6 pt-16 sm:pt-24 pb-20">{children}</main>
    </div>
  );
}

export default function ShareStory() {
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [form, setForm] = useState({ first_name: "", quote: "", consent: false });
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/story-invite/${token}`)
      .then((r) => {
        setForm((f) => ({ ...f, first_name: r.data.first_name }));
        setState(r.data.responded ? "done" : "form");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setState("sending");
    try {
      await api.post(`/story-invite/${token}`, form);
      setState("done");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send just now.");
      setState("form");
    }
  };

  if (state === "loading") return <Shell><Loader2 className="h-6 w-6 animate-spin text-bone-400" /></Shell>;
  if (state === "invalid") return <Shell><p className="font-serif text-2xl text-bone-100" data-testid="share-story-invalid">This link has expired or isn't valid.</p></Shell>;
  if (state === "done") {
    return (
      <Shell>
        <h1 className="font-serif text-4xl text-bone-100 mb-4" data-testid="share-story-thanks">Thank you.</h1>
        <p className="font-sans text-bone-300 leading-relaxed">Danny reads every one before anything goes on the site.</p>
      </Shell>
    );
  }

  return (
    <Shell>
      <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-5">Listener stories</p>
      <h1 className="font-serif text-4xl sm:text-5xl text-bone-100 leading-tight mb-4">One honest line.</h1>
      <p className="font-sans text-bone-300 leading-relaxed mb-10">What changed since you started speaking into Umbra? A single sentence is perfect.</p>
      <form onSubmit={submit} className="space-y-5" data-testid="share-story-form">
        <Textarea
          required minLength={5} maxLength={280} value={form.quote}
          onChange={(e) => setForm({ ...form, quote: e.target.value })}
          placeholder="e.g. I finished Chapter Three by speaking for ten minutes at night."
          data-testid="share-story-quote"
          className="min-h-[110px] bg-obsidian-800 border-obsidian-600 rounded-none text-bone-100 text-base"
        />
        <p className="font-sans text-xs text-bone-500 text-right">{form.quote.length}/280</p>
        <Input
          required maxLength={40} value={form.first_name}
          onChange={(e) => setForm({ ...form, first_name: e.target.value })}
          placeholder="First name to show"
          data-testid="share-story-name"
          className="bg-obsidian-800 border-obsidian-600 rounded-none text-bone-100"
        />
        <label className="flex items-start gap-3 font-sans text-sm text-bone-300 leading-relaxed cursor-pointer">
          <Checkbox checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: v === true })} data-testid="share-story-consent" className="mt-0.5" />
          I give Umbra permission to show this quote with my first name on shadowspeakjournal.com.
        </label>
        {error && <p className="font-sans text-sm text-red-400" data-testid="share-story-error">{error}</p>}
        <Button type="submit" disabled={!form.consent || state === "sending"} data-testid="share-story-submit" className="rounded-none bg-terracotta hover:bg-terracotta-deep text-obsidian-900 uppercase tracking-wider text-xs py-6 px-6">
          {state === "sending" && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Share my line
        </Button>
      </form>
    </Shell>
  );
}
